import { FC, useState } from "react";
import classNames from "classnames";

import { ManagedAccount, ManagedAccountBrand } from "../../../../store/agency/types";
import {
  getDisplayName,
  formatScore,
  formatScoreTrend,
  shouldShowClientEmail,
  getClientBrands,
  formatTimeAgo,
} from "../../utils";
import ClientActionsMenu from "../ClientActionsMenu/ClientActionsMenu";
import tableStyles from "../../../../components/SourceTable/SourceTable.module.css";

import styles from "./QuickStatsTable.module.css";

interface IQuickStatsTableProps {
  clients: ManagedAccount[];
  onActAs?: (client: ManagedAccount) => void;
  onRename?: (client: ManagedAccount) => void;
  onDetach?: (client: ManagedAccount) => void;
}

const columns = [
  { key: "client", label: "Client" },
  { key: "brand", label: "Brand" },
  { key: "score", label: "Visibility" },
  { key: "trend", label: "Trend" },
  { key: "prompts", label: "Prompts" },
  { key: "updated", label: "Updated" },
] as const;

const getColumnHeaderClass = (key: (typeof columns)[number]["key"]) =>
  classNames(
    key === "client" && styles["clients-table__col_is-client"],
    key === "brand" && styles["clients-table__col_is-brand"],
    key === "updated" && styles["clients-table__updated"],
  );

interface ClientNameCellProps {
  client: ManagedAccount;
  isClickable: boolean;
  onClick: () => void;
}

const ClientNameCell: FC<ClientNameCellProps> = ({ client, isClickable, onClick }) => {
  const displayName = getDisplayName(client);
  const showEmail = shouldShowClientEmail(client);

  return (
    <div className={styles["clients-table__client"]}>
      {isClickable ? (
        <button
          type="button"
          className={styles["clients-table__name-button"]}
          onClick={onClick}
        >
          {displayName}
        </button>
      ) : (
        <span className={styles["clients-table__name"]}>{displayName}</span>
      )}
      {showEmail && (
        <span className={styles["clients-table__email"]}>{client.clientEmail}</span>
      )}
    </div>
  );
};

interface BrandStatsRowProps {
  client: ManagedAccount;
  brand: ManagedAccountBrand | null;
  rowSpan: number;
  isFirstRow: boolean;
  clientIndex: number;
  isDetached: boolean;
  isNameClickable: boolean;
  showActions: boolean;
  isMenuOpen: boolean;
  onMenuOpenChange: (isOpen: boolean) => void;
  onClientClick: () => void;
  onActAs?: (client: ManagedAccount) => void;
  onRename?: (client: ManagedAccount) => void;
  onDetach?: (client: ManagedAccount) => void;
}

const BrandStatsRow: FC<BrandStatsRowProps> = ({
  client,
  brand,
  rowSpan,
  isFirstRow,
  clientIndex,
  isDetached,
  isNameClickable,
  showActions,
  isMenuOpen,
  onMenuOpenChange,
  onClientClick,
  onActAs,
  onRename,
  onDetach,
}) => {
  const trend = formatScoreTrend(brand?.scoreTrend ?? null);
  const promptCount = brand?.promptCount ?? client.promptCount;
  const updatedLabel = brand?.updatedAt
    ? formatTimeAgo(brand.updatedAt)
    : "—";

  return (
    <tr
      className={classNames(
        tableStyles["source_table__body_row"],
        isDetached && styles["clients-table__row_is-detached"],
      )}
    >
      {isFirstRow && <td rowSpan={rowSpan} className={styles["clients-table__col_is-index"]}>{clientIndex + 1}</td>}
      {isFirstRow && (
        <td rowSpan={rowSpan} className={styles["clients-table__col_is-client"]}>
          <ClientNameCell
            client={client}
            isClickable={isNameClickable}
            onClick={onClientClick}
          />
        </td>
      )}
      <td className={styles["clients-table__col_is-brand"]}>
        {brand ? (
          <span className={styles["clients-table__brand-name"]}>{brand.name}</span>
        ) : (
          <span className={styles["clients-table__muted"]}>—</span>
        )}
      </td>
      <td>{brand ? formatScore(brand.currentScore) : <span className={styles["clients-table__muted"]}>—</span>}</td>
      <td>
        {brand ? (
          <span className={styles[`clients-table__trend_is-${trend.direction}`]}>
            {trend.text}
          </span>
        ) : (
          <span className={styles["clients-table__muted"]}>—</span>
        )}
      </td>
      <td>{promptCount}</td>
      <td className={classNames(styles["clients-table__muted"], styles["clients-table__updated"])}>
        {brand ? updatedLabel : "—"}
      </td>
      {isFirstRow && showActions && (
        <td
          rowSpan={rowSpan}
          className={classNames(
            styles["clients-table__actions-cell"],
            styles["clients-table__col_is-actions"],
          )}
        >
          {!isDetached && (
            <ClientActionsMenu
              client={client}
              isOpen={isMenuOpen}
              onOpenChange={onMenuOpenChange}
              onActAs={onActAs}
              onRename={onRename}
              onDetach={onDetach}
            />
          )}
        </td>
      )}
    </tr>
  );
};

const QuickStatsTable: FC<IQuickStatsTableProps> = ({ clients, onActAs, onRename, onDetach }) => {
  const [openMenuClientId, setOpenMenuClientId] = useState<number | null>(null);
  const showActions = Boolean(onActAs || onRename || onDetach);

  return (
    <div className={tableStyles["source_table_wrapper"]}>
      <table className={classNames(tableStyles["source_table"], styles["clients-table"])}>
        <thead>
          <tr className={tableStyles["source_table__header_row"]}>
            <th className={styles["clients-table__col_is-index"]}>#</th>
            {columns.map((col) => (
              <th key={col.key} className={getColumnHeaderClass(col.key)}>
                {col.label}
              </th>
            ))}
            {showActions && (
              <th aria-label="Actions" className={styles["clients-table__col_is-actions"]} />
            )}
          </tr>
        </thead>
        <tbody>
          {clients.map((client, clientIndex) => {
            const brands = getClientBrands(client);
            const rows = brands.length > 0 ? brands : [null];
            const rowSpan = rows.length;
            const isDetached = client.status === "detached";
            const isNameClickable = Boolean(onActAs) && !isDetached;

            return rows.map((brand, brandIndex) => (
              <BrandStatsRow
                key={brand ? `${client.id}-${brand.id}` : `${client.id}-empty`}
                client={client}
                brand={brand}
                rowSpan={rowSpan}
                isFirstRow={brandIndex === 0}
                clientIndex={clientIndex}
                isDetached={isDetached}
                isNameClickable={isNameClickable}
                showActions={showActions}
                isMenuOpen={openMenuClientId === client.id}
                onMenuOpenChange={(isOpen) => setOpenMenuClientId(isOpen ? client.id : null)}
                onClientClick={() => onActAs?.(client)}
                onActAs={onActAs}
                onRename={onRename}
                onDetach={onDetach}
              />
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuickStatsTable;
